/* eslint-disable react/prop-types */
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Spin,
  Tabs,
  Typography,
} from 'antd';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import Table from 'components/Table';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { isMutable } from 'utils/estadosGlobales';
import { onError } from 'utils/handlers';

import {
  baseURI,
  deleteUnidadEjecutora,
  getUnidadesEjecutoras,
  patchUnidadEjecutora,
  postUnidadEjecutora,
} from 'api/pae/unidadesEjecutoras';
import LadaNumero from 'components/LadaNumero';
import { makeGetOnFinish, makeOnChangeTabKey } from 'utils/tabsManagement';
import makeMessager from 'utils/complexFormsMessages';
import Direccion from 'components/Direccion';
import { EnvironmentOutlined, HomeOutlined } from '@ant-design/icons';
import keyGenerator from 'utils/keyGenerator';
import { capitalize } from 'utils/normalizers';

export const permissionConfig = {
  permissionModel: 'unidadejecutorapae',
};

const UnidadesEjecutoras = ({ permission, addressPermission }) => {
  const { estadosGlobales } = useSelector((state) => state.catalogs);
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [mutable, setMutable] = useState(true);
  const [currentTabKey, setCurrentTabKey] = useState('general');
  const [formAddress, setFormAddress] = useState({});

  const basicKeys = ['general'];
  const complexForms = [
    { key: 'direccion', formInstance: formAddress, permission: addressPermission },
  ];

  const fetchData = async () => {
    setLoading(true);
    const res = await getUnidadesEjecutoras(null, false);
    setData(res);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;

    const fetchAll = async () => {
      setLoading(true);
      const _data = await getUnidadesEjecutoras();
      if (mounted) {
        setData(_data);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setSelectedRowKeys([]);
    form.resetFields();
    setCurrentTabKey('general');
  };

  const onClickAdd = () => {
    onCancel();
    keyGenerator(data, form);
    setVisible(true);
  };

  const onClickEdit = () => {
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const onFinish = async (_continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      const saved = await (key ? patchUnidadEjecutora(key, values, false)
        : postUnidadEjecutora(values));
      if (saved) {
        await fetchData();
        if (_continue) {
          if (!key) {
            setSelectedRowKeys([saved.id]);
          }
          await onClickEdit(saved.id);
        } else {
          onCancel();
        }
        return saved;
      }
      return saved;
    } catch (error) {
      onError(error, setLoading, [form], setCurrentTabKey);
    } finally {
      setLoading(false);
    }
    return null;
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteUnidadEjecutora(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
    setMutable(isMutable(record));
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 150,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 300,
      ellipsis: true,
      render: (text) => (
        <Typography.Text ellipsis={{ tooltip: text }}>
          {text}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Correo Electrónico',
      dataIndex: 'correo_electronico',
      key: 'correo_electronico',
      width: 200,
      ellipsis: true,
      render: (text) => (
        <Typography.Text ellipsis={{ tooltip: text }}>
          {text || '-'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Número de Celular',
      dataIndex: 'numero_de_celular',
      key: 'numero_de_celular',
      width: 250,
      render: (text) => text || '-',
    },
    {
      titleText: 'Número de Teléfono',
      dataIndex: 'numero_de_telefono',
      key: 'numero_de_telefono',
      width: 250,
      render: (text) => text || '-',
    },
    {
      titleText: 'Representante Legal',
      dataIndex: 'representante_legal',
      key: 'representante_legal',
      width: 200,
      ellipsis: true,
      render: (text) => (
        <Typography.Text ellipsis={{ tooltip: text }}>
          {text || '-'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Observaciones',
      dataIndex: 'observaciones',
      key: 'observaciones',
      width: 250,
      ellipsis: true,
      render: (text) => (
        <Typography.Text ellipsis={{ tooltip: text }}>
          {text || '-'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 150,
      render: (value) => {
        const estado = estadosGlobales.find((eg) => eg.id === value);
        return (
          <Typography.Text type={estado?.descripcion?.toLowerCase().includes('activo') ? 'success' : 'default'}>
            {estado?.descripcion || '-'}
          </Typography.Text>
        );
      },
    },
  ];

  const rules = {
    required: [{ required: true }],
  };

  const showMsg = makeMessager(setCurrentTabKey, fetchData);

  const onChangeTabKey = makeOnChangeTabKey(
    basicKeys,
    complexForms,
    currentTabKey,
    setCurrentTabKey,
    showMsg,
  );

  const getOnFinish = makeGetOnFinish(basicKeys, complexForms, currentTabKey, onFinish);
  const _onFinish = getOnFinish();

  return (
    <Row align="center" justify="center" className="container">
      <Spin
        tip="Cargando..."
        spinning={loading}
      >
        {!visible && (
          <Table
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            allowColumnSelector={false}
            controls={{
              onClickAdd,
              onClickEdit,
              onClickDelete,
            }}
            rowKey="id"
            permission={permission}
            hasAllViewPermission
            deleteText="Cancelar"
          />
        )}
        {visible && (
          <Card
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Unidad Ejecutora`}
            extra={(
              <FormSubmitControls
                onFinish={(_, _continue) => _onFinish(_continue)}
                onCancel={onCancel}
                loading={loading}
                mutable={mutable}
                permission={permission}
                selectedRowKeys={selectedRowKeys}
                callback={(id) => {
                  const [key] = selectedRowKeys;
                  const normalized = data.map((e) => {
                    if (e.id === key) {
                      const match = estadosGlobales.find((eg) => eg.id === id);
                      form.setFieldsValue({ estados_globales: match.id });
                      setMutable(true);
                      return { ...e, estados_globales: id };
                    }
                    return e;
                  });
                  setData(normalized);
                }}
              />
            )}
            bordered={false}
          >
            <Tabs
              onChange={onChangeTabKey}
              activeKey={currentTabKey}
              type="card"
            >
              <Tabs.TabPane
                tab={(
                  <span>
                    <HomeOutlined />
                    General
                  </span>
                )}
                key="general"
              >
                <Form
                  layout="vertical"
                  form={form}
                  onFinish={(_, _continue) => _onFinish(_continue)}
                  scrollToFirstError
                  name="general"
                  initialValues={{ estados_globales: 1 }}
                >

                  {/* Sección 1: Información de Identificación */}
                  <Row gutter={[10, 10]}>
                    <Col xs={24} md={8}>
                      <Form.Item
                        name="clave"
                        label="Clave"
                      >
                        <Input disabled={selectedRowKeys.length} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={16}>
                      <Form.Item
                        name="descripcion"
                        normalize={capitalize}
                        rules={rules.required}
                        label="Descripción"
                      >
                        <Input.TextArea
                          disabled={!mutable}
                          allowClear
                          maxLength={300}
                          autoSize={{ minRows: 2, maxRows: 4 }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Sección 2: Información de Contacto */}
                  <Row gutter={[10, 10]}>
                    <Col xs={24} md={8}>
                      <Form.Item
                        name="correo_electronico"
                        label="Correo Electrónico"
                      >
                        <Input
                          disabled={!mutable}
                          allowClear
                        />
                      </Form.Item>
                    </Col>
                    <LadaNumero
                      disabled={!mutable}
                      breakPoints={{
                        md: 24,
                        lg: 8,
                        xl: 8,
                        xxl: 8,
                      }}
                      names={{
                        lada: 'lada_de_celular',
                        numero_de_telefono: 'numero_de_celular',
                      }}
                    />
                    <LadaNumero
                      disabled={!mutable}
                      breakPoints={{
                        md: 24,
                        lg: 8,
                        xl: 8,
                        xxl: 8,
                      }}
                      names={{
                        lada: 'lada_de_telefono',
                        numero_de_telefono: 'numero_de_telefono',
                      }}
                    />
                    <Col xs={24} md={8}>
                      <Form.Item
                        name="representante_legal"
                        label="Representante Legal"
                      >
                        <Input
                          disabled={!mutable}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Sección 3: Configuración y Estado */}
                  <Row gutter={[10, 10]}>
                    <Col xs={24} md={16}>
                      <Form.Item
                        name="observaciones"
                        label="Observaciones"
                        normalize={capitalize}
                        hasFeedback
                      >
                        <Input.TextArea
                          allowClear
                          disabled={!mutable}
                          autoSize={{ minRows: 3, maxRows: 6 }}
                          maxLength={500}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item
                        name="estados_globales"
                        label="Estado"
                        hasFeedback
                      >
                        <Select
                          dataSource={estadosGlobales}
                          allowClear
                          disabled
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Form>
              </Tabs.TabPane>
              {addressPermission.view && (
              <>
                <Tabs.TabPane
                  tab={(
                    <span>
                      <EnvironmentOutlined />
                      Dirección
                    </span>
                  )}
                  key="direccion"
                  forceRender={!!selectedRowKeys?.length}
                  disabled={!selectedRowKeys.length}
                >
                  <Direccion
                    setFormAddress={setFormAddress}
                    currentURI={baseURI}
                    parentID={selectedRowKeys[0]}
                    setLoading={setLoading}
                    setCurrentTabKey={setCurrentTabKey}
                    addressPropName="direccion"
                    callback={(values, _continue) => {
                      if (!_continue) {
                        onCancel();
                      } else {
                        const [key] = selectedRowKeys;
                        const clone = [...data.results];
                        const results = clone.map((e) => (e.id === key
                          ? ({ ...e, direccion: values?.id }) : e));
                        setData({ ...data, results });
                      }
                    }}
                    permission={addressPermission}
                    mutable={mutable}
                  />
                </Tabs.TabPane>
              </>
              )}
            </Tabs>
          </Card>
        )}

        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => {
            setVisibleAlert(false);
            onCancel();
          }}
          visible={visibleAlert}
          content={` la unidad ejecutora ${form.getFieldValue('descripcion')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default UnidadesEjecutoras;
